import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LinearConstraintData as ConstraintData } from '@blisspointmedia/bpm-types/dist/OptimizationConstraints';
import { OPTIMIZATION_TYPE_MAP, OptimizationType } from './linear-optimizations-constants';
import { LinearOptimizationsLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { withIndexes } from '../../utils/optimization-utils';

const jsonHeaders = { Accept: 'application/json', 'Content-Type': 'application/json' };

// `audience` didn't exist on older constraints -- impute an empty array rather than crash.
const withIndexedSections = (
  constraints: ConstraintData['constraints']
): ConstraintData['constraints'] => ({
  ...constraints,
  budget: withIndexes(constraints.budget),
  spot: withIndexes(constraints.spot),
  audience: withIndexes(constraints.audience ?? []),
  avoid: withIndexes(constraints.avoid),
  creativesAllowed: withIndexes(constraints.creativesAllowed),
  networkCreativesDisallowed: withIndexes(constraints.networkCreativesDisallowed)
});

export const validateLinearConstraint = async (body: ConstraintData): Promise<ConstraintData> => {
  const result = await LinearOptimizationsLambdaFetch('/validate_constraints', {
    method: 'POST',
    headers: jsonHeaders,
    body
  });
  const validated = await awaitJSON<ConstraintData>(result);
  return {
    ...validated,
    optimizationType:
      OPTIMIZATION_TYPE_MAP[validated.optimization_type_id ?? -1] ?? OptimizationType.BUYING
  };
};

export const useConstraintData = (company: string, constraintId: string) =>
  useQuery<ConstraintData>({
    queryKey: ['linearConstraintData', company, constraintId],
    queryFn: async () => {
      const result = await LinearOptimizationsLambdaFetch('/constraints', {
        params: { company, name: constraintId }
      });
      const fetched = await awaitJSON<ConstraintData>(result);
      return {
        ...fetched,
        optimizationType:
          OPTIMIZATION_TYPE_MAP[fetched.optimization_type_id ?? -1] ?? OptimizationType.BUYING,
        constraints: withIndexedSections(fetched.constraints)
      };
    },
    enabled: Boolean(company && constraintId)
  });

export const useSaveConstraint = (company: string, constraintId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (constraintData: ConstraintData) => {
      const saved = await LinearOptimizationsLambdaFetch('/constraints', {
        method: 'POST',
        headers: jsonHeaders,
        body: constraintData
      });
      const result = await awaitJSON<ConstraintData>(saved);
      return {
        ...result,
        optimizationType: constraintData.optimizationType,
        constraints: withIndexedSections(result.constraints)
      };
    },
    onSuccess: saved =>
      queryClient.setQueryData(['linearConstraintData', company, constraintId], saved)
  });
};
